// v1.7.0 · CRM · Oportunidades: tablero por etapas con arrastre, vista de lista, alta/edición y estados
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Plus, Search, LayoutGrid, List as ListIcon, Trash2 } from 'lucide-react';
import { api, errMsg, money, fdate, StatusBadge, STATUS, Modal, inputCls, btn, Field, OwnOwner, Empty } from './ui';

export function ContactPicker({ value, label, onChange }) {   // v1.8.0 · también lo usa Conversaciones
  const [q, setQ] = useState('');
  const [opts, setOpts] = useState([]);
  useEffect(() => {
    if (!q.trim()) { setOpts([]); return undefined; }
    const t = setTimeout(async () => {
      try { setOpts((await api.get('/contacts', { q, page_size: 8 })).items); } catch { setOpts([]); }
    }, 250);
    return () => clearTimeout(t);
  }, [q]);
  if (value) {
    return (
      <div className="flex items-center justify-between rounded-lg border border-gray-300 px-3 py-2 text-sm">
        <span className="truncate">{label || 'Contacto'}</span>
        <button type="button" className="text-xs font-semibold text-[#2F3D8E]" data-testid="opp-contact-change" onClick={(e) => { e.preventDefault(); onChange(null, ''); }}>Cambiar</button>
      </div>
    );
  }
  return (
    <div className="relative">
      <input className={inputCls} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar contacto por nombre, correo o teléfono" />
      {opts.length > 0 && (
        <ul className="absolute z-10 mt-1 max-h-56 w-full overflow-y-auto rounded-lg border bg-white shadow-lg">
          {opts.map((c) => (
            <li key={c.id}><button type="button" className="block w-full px-3 py-2 text-left text-sm hover:bg-gray-50" data-testid="opp-contact-option"
                                   onClick={(e) => { e.preventDefault(); onChange(c.id, c.name); setQ(''); setOpts([]); }}>
              <b>{c.name}</b> <span className="text-gray-500">{c.email || c.phone}</span>
            </button></li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function OppForm({ meta, pipeline, opp, presetContact, onClose, onSaved }) {
  const isNew = !opp?.id;
  const [o, setO] = useState(() => ({
    name: opp?.name || '', value: opp?.value ?? 0, source: opp?.source || '', owner_id: opp?.owner_id || '',
    stage_id: opp?.stage_id || pipeline.stages[0]?.id, status: opp?.status || 'open', lost_reason: opp?.lost_reason || '',
    contact_id: opp?.contact_id ?? presetContact?.id ?? null, contact_name: opp?.contact_name ?? presetContact?.name ?? '',
  }));
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setO({ ...o, [k]: e.target.value });

  const save = async () => {
    setSaving(true);
    const body = { name: o.name, value: Number(o.value) || 0, source: o.source, owner_id: o.owner_id || null,
                   stage_id: o.stage_id, status: o.status, contact_id: o.contact_id };
    try {
      const r = isNew ? await api.post('/opportunities', { ...body, pipeline_id: pipeline.id })
                      : await api.put(`/opportunities/${opp.id}`, { ...body, lost_reason: o.status === 'lost' ? o.lost_reason : '' });
      toast.success(isNew ? 'Oportunidad creada' : 'Oportunidad actualizada');
      onSaved(r, o.status);
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };
  const remove = async () => {
    if (!window.confirm('¿Eliminar esta oportunidad?')) return;
    try { await api.del(`/opportunities/${opp.id}`); toast.success('Oportunidad eliminada'); onSaved(null); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <Modal title={isNew ? 'Nueva oportunidad' : 'Editar oportunidad'} onClose={onClose}
           footer={<>
             {!isNew && <button className={`${btn.danger} mr-auto`} onClick={remove}><Trash2 size={14} /> Eliminar</button>}
             <button className={btn.ghost} onClick={onClose}>Cancelar</button>
             <button className={btn.primary} disabled={saving || !o.name.trim()} onClick={save}>{saving ? 'Guardando…' : 'Guardar'}</button>
           </>}>
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="sm:col-span-2"><Field label="Nombre de la oportunidad"><input className={inputCls} value={o.name} onChange={set('name')} autoFocus /></Field></div>
        <div className="sm:col-span-2"><Field label="Contacto" group>
          <ContactPicker value={o.contact_id} label={o.contact_name} onChange={(id, name) => setO({ ...o, contact_id: id, contact_name: name })} />
        </Field></div>
        <Field label="Valor (USD)"><input type="number" min="0" step="0.01" className={inputCls} value={o.value} onChange={set('value')} /></Field>
        <Field label="Etapa">
          <select className={inputCls} value={o.stage_id} onChange={set('stage_id')}>
            {pipeline.stages.map((s) => <option key={s.id} value={s.id}>{s.name} · {s.probability} %</option>)}
          </select>
        </Field>
        <Field label="Estado">
          <select className={inputCls} value={o.status} onChange={set('status')}>
            {Object.entries(STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
        </Field>
        {meta.scope === 'own' ? <OwnOwner meta={meta} /> : (   // v1.12.1
        <Field label="Responsable">
          <select className={inputCls} value={o.owner_id} onChange={set('owner_id')}>
            <option value="">Sin asignar</option>{meta.users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
        </Field>
        )}
        <Field label="Fuente">
          <input className={inputCls} value={o.source} onChange={set('source')} list="crm-opp-sources" />
          <datalist id="crm-opp-sources">{meta.sources.map((s) => <option key={s} value={s} />)}</datalist>
        </Field>
        {o.status === 'lost' && !isNew && (
          <div className="sm:col-span-2"><Field label="Motivo de pérdida"><input className={inputCls} value={o.lost_reason} onChange={set('lost_reason')} /></Field></div>
        )}
      </div>
    </Modal>
  );
}

function OppCard({ o, users, onOpen, onDragStart, stages, onMoveTo }) {
  return (
    <div draggable onDragStart={(e) => onDragStart(e, o)} onClick={() => onOpen(o)} data-testid="opp-card"
         className="cursor-grab rounded-lg border border-gray-200 bg-white p-2.5 shadow-sm transition hover:border-[#2F3D8E]/40 hover:shadow active:cursor-grabbing">
      <div className="flex items-start justify-between gap-2">
        <div className="text-[13px] font-semibold leading-snug text-[#0F2A3A]">{o.name}</div>
        {o.status !== 'open' && <StatusBadge status={o.status} />}
      </div>
      {o.contact_id
        ? <Link to={`/crm/contactos/${o.contact_id}`} onClick={(e) => e.stopPropagation()} className="mt-0.5 block truncate text-xs text-[#2F3D8E] hover:underline">{o.contact_name}</Link>
        : <div className="mt-0.5 text-xs text-gray-400">Sin contacto</div>}
      <div className="mt-1.5 flex items-center justify-between text-xs">
        <b className="text-gray-900">{money(o.value)}</b>
        <span className="truncate pl-2 text-gray-500">{users[o.owner_id] || ''}</span>
      </div>
      {/* En pantallas táctiles el arrastre HTML5 no funciona: selector para mover */}
      <select className="mt-2 w-full rounded border border-gray-200 bg-gray-50 px-1.5 py-1 text-[11px] md:hidden" value={o.stage_id}
              onClick={(e) => e.stopPropagation()} onChange={(e) => onMoveTo(o, e.target.value)} aria-label="Mover a etapa">
        {stages.map((s) => <option key={s.id} value={s.id}>Mover a: {s.name}</option>)}
      </select>
    </div>
  );
}

export default function CrmOpportunities({ meta }) {
  const [pipelineId, setPipelineId] = useState(meta.pipelines[0]?.id);
  const pipeline = meta.pipelines.find((p) => p.id === pipelineId) || meta.pipelines[0];
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState('open');
  const [q, setQ] = useState('');
  const [owner, setOwner] = useState('');
  const [view, setView] = useState('board');
  const [edit, setEdit] = useState(null);
  const [dragOver, setDragOver] = useState(null);
  const users = useMemo(() => Object.fromEntries(meta.users.map((u) => [u.id, u.name])), [meta.users]);

  const load = useCallback(async () => {
    if (!pipeline) return;
    try { setItems((await api.get('/opportunities', { pipeline_id: pipeline.id, status, q, owner_id: owner })).items); }
    catch (e) { toast.error(errMsg(e)); }
  }, [pipeline, status, q, owner]);
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [load]);

  const byStage = useMemo(() => {
    const m = Object.fromEntries((pipeline?.stages || []).map((s) => [s.id, []]));
    items.forEach((o) => { (m[o.stage_id] = m[o.stage_id] || []).push(o); });
    Object.values(m).forEach((arr) => arr.sort((a, b) => a.position - b.position));
    return m;
  }, [items, pipeline]);

  const move = async (o, stageId, beforeId) => {
    const col = (byStage[stageId] || []).filter((x) => x.id !== o.id);
    const idx = beforeId ? col.findIndex((x) => x.id === beforeId) : col.length;
    const prev = col[idx - 1]?.position; const next = col[idx]?.position;
    let pos;
    if (prev === undefined && next === undefined) pos = 1024;
    else if (prev === undefined) pos = next / 2;
    else if (next === undefined) pos = prev + 1024;
    else pos = (prev + next) / 2;
    const before = items;
    setItems(items.map((x) => (x.id === o.id ? { ...x, stage_id: stageId, position: pos } : x)));
    try { await api.patch(`/opportunities/${o.id}/move`, { stage_id: stageId, position: pos }); }
    catch (e) { setItems(before); toast.error(errMsg(e)); }
  };

  const onDragStart = (e, o) => { e.dataTransfer.setData('text/plain', o.id); e.dataTransfer.effectAllowed = 'move'; };
  const onDrop = (e, stageId) => {
    e.preventDefault(); setDragOver(null);
    const id = e.dataTransfer.getData('text/plain');
    const o = items.find((x) => x.id === id);
    if (!o) return;
    const card = e.target.closest('[data-opp]');
    const beforeId = card && card.dataset.opp !== id ? card.dataset.opp : null;
    move(o, stageId, beforeId);
  };

  if (!pipeline) return <Empty title="No hay embudos">Cree un embudo en Configuración.</Empty>;
  const totalValue = items.reduce((s, o) => s + (o.value || 0), 0);
  const forecast = items.filter((o) => o.status === 'open').reduce((s, o) => s + (o.value || 0) * ((pipeline.stages.find((st) => st.id === o.stage_id)?.probability || 0) / 100), 0);

  return (
    <div className="space-y-3" data-testid="crm-opportunities">
      <div className="flex flex-wrap items-center gap-2">
        <select className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-semibold" value={pipeline.id} onChange={(e) => setPipelineId(e.target.value)} aria-label="Embudo">
          {meta.pipelines.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        <select className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm" value={status} onChange={(e) => setStatus(e.target.value)} aria-label="Estado">
          <option value="">Todos los estados</option>{Object.entries(STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <select className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm" value={owner} onChange={(e) => setOwner(e.target.value)} aria-label="Responsable">
          <option value="">Todos los responsables</option><option value="unassigned">Sin asignar</option>
          {meta.users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
        </select>
        <div className="relative min-w-[180px] flex-1">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input className={`${inputCls} pl-9`} placeholder="Buscar oportunidad o contacto" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <div className="flex overflow-hidden rounded-lg border border-gray-300">
          <button className={`px-2.5 py-2 ${view === 'board' ? 'bg-[#2F3D8E] text-white' : 'bg-white text-gray-600'}`} onClick={() => setView('board')} aria-label="Tablero"><LayoutGrid size={15} /></button>
          <button className={`px-2.5 py-2 ${view === 'list' ? 'bg-[#2F3D8E] text-white' : 'bg-white text-gray-600'}`} onClick={() => setView('list')} aria-label="Lista"><ListIcon size={15} /></button>
        </div>
        <button className={btn.primary} onClick={() => setEdit({})} data-testid="opp-new"><Plus size={15} /> Oportunidad</button>
      </div>
      <div className="flex flex-wrap gap-4 text-sm text-gray-600">
        <span><b className="text-gray-900">{items.length}</b> oportunidades</span>
        <span>Valor: <b className="text-gray-900">{money(totalValue)}</b></span>
        <span>Pronóstico ponderado: <b className="text-gray-900">{money(forecast)}</b></span>
      </div>

      {view === 'board' ? (
        <div className="flex gap-3 overflow-x-auto pb-3">
          {pipeline.stages.map((s) => {
            const col = byStage[s.id] || [];
            return (
              <div key={s.id} data-testid="stage-col" data-stage={s.id} onDragOver={(e) => { e.preventDefault(); setDragOver(s.id); }} onDragLeave={() => setDragOver(null)} onDrop={(e) => onDrop(e, s.id)}
                   className={`flex w-[272px] shrink-0 flex-col rounded-xl border p-2 transition ${dragOver === s.id ? 'border-[#2F3D8E] bg-[#2F3D8E]/5' : 'border-gray-200 bg-gray-50'}`}>
                <div className="mb-2 px-1">
                  <div className="flex items-center justify-between text-[13px] font-bold text-[#0F2A3A]"><span className="truncate">{s.name}</span><span className="text-xs font-semibold text-gray-500">{col.length}</span></div>
                  <div className="text-xs text-gray-500">{money(col.reduce((a, o) => a + (o.value || 0), 0))} · {s.probability} %</div>
                </div>
                <div className="flex min-h-[80px] flex-col gap-2">
                  {col.map((o) => (
                    <div key={o.id} data-opp={o.id}>
                      <OppCard o={o} users={users} stages={pipeline.stages} onOpen={setEdit} onDragStart={onDragStart} onMoveTo={(x, sid) => move(x, sid, null)} />
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
          <table className="w-full min-w-[760px] text-sm">
            <thead className="bg-gray-50 text-left text-xs text-gray-500"><tr>
              {['Oportunidad', 'Contacto', 'Etapa', 'Estado', 'Valor', 'Responsable', 'Fuente', 'Creada'].map((h) => <th key={h} className="px-3 py-2.5 font-semibold">{h}</th>)}
            </tr></thead>
            <tbody>
              {items.map((o) => (
                <tr key={o.id} className="cursor-pointer border-t hover:bg-gray-50" onClick={() => setEdit(o)}>
                  <td className="px-3 py-2 font-semibold text-[#0F2A3A]">{o.name}</td>
                  <td className="px-3 py-2">{o.contact_name || '—'}</td>
                  <td className="px-3 py-2">{pipeline.stages.find((s) => s.id === o.stage_id)?.name || '—'}</td>
                  <td className="px-3 py-2"><StatusBadge status={o.status} /></td>
                  <td className="px-3 py-2 font-semibold">{money(o.value)}</td>
                  <td className="px-3 py-2">{users[o.owner_id] || '—'}</td>
                  <td className="px-3 py-2">{o.source || '—'}</td>
                  <td className="whitespace-nowrap px-3 py-2 text-gray-500">{fdate(o.created_at)}</td>
                </tr>
              ))}
              {items.length === 0 && <tr><td colSpan={8} className="px-3 py-10 text-center text-gray-500">Sin oportunidades con estos filtros.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {edit && <OppForm meta={meta} pipeline={pipeline} opp={edit.id ? edit : null} onClose={() => setEdit(null)}
                        onSaved={(r, st) => {
                          setEdit(null); load();
                          // v1.7.1 · avisar si la oportunidad sale del tablero por el filtro de estado
                          if (r && status && st !== status) toast.info(`Quedó como «${STATUS[st].label}». Elija «Todos los estados» para verla en el tablero.`);
                        }} />}
    </div>
  );
}
